import { FC, PropsWithChildren, useCallback, useEffect, useRef, useState } from "react"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { forceRefreshUser, prefetchCurrentUser } from "@/api/users-api"
import { axiosClient } from "@/api/operations"
import {
	createAxiosClientResponseErrorHandler,
	createAxiosPSClientResponseErrorHandler,
} from "@/api/auth-interceptors"
import { ProjectService } from "@/store/project/projects-service"
import { resetDialogs } from "@/store/dialogs-state"
import { isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import { ElectronPreLogin } from "./electron/ElectronPreLogin"
import {
	clearPSAccessToken,
	isPSAccessTokenRefreshError,
	requestPSAccessToken,
	returnToAtom,
	type PSAccessTokenRequestContext,
} from "@/api/OAuthClient"
import { useAuth } from "@/utils/auth"
import { useAtom } from "jotai"
import { Sentry } from "@/telemetry/sentry"
import { axiosPSClient } from "@/api/PSApiOperations"
import { isAccountMigratedAtom, isAuthReadyAtom } from "@/store/auth-state"
import { store } from "@/store/store"
import { AuthPage } from "@/components/AuthPage"
import { isElectronApp } from "@/utils/electron"
import { getTelemetryRequestPath } from "@/telemetry/request-path"

/**
Missing refresh token error message fragment from `auth0-spa-js`.
See: https://github.com/auth0/auth0-spa-js/blob/8653181d23ecea17729da203253d4fa4d17d8ef6/src/constants.ts#L37
*/
const MISSING_REFRESH_TOKEN_ERROR_MESSAGE = "Missing Refresh Token" // i18n-ignore: internal error identifier

/**
Invalid refresh token error message fragment from `auth0-spa-js`.
See: https://github.com/auth0/auth0-spa-js/blob/8653181d23ecea17729da203253d4fa4d17d8ef6/src/constants.ts#L42
*/
const INVALID_REFRESH_TOKEN_ERROR_MESSAGE = "invalid refresh token"
const MISSING_STATE_RETRY_KEY = "pixieset-missing-state-retry"

export const AppAuthGuard: FC<PropsWithChildren> = ({ children }) => {
	const { t } = useTranslation("components")
	const { isAuthenticated, isLoading, getAccessToken, loginWithRedirect, isURLAuthenticated } =
		useAuth()

	const [initialized, setInitialized] = useState(false)
	const [showAuthPage, setShowAuthPage] = useState(false)
	const [showPreLogin, setShowPreLogin] = useState(false)
	const isProcessingAuthCodeRef = useRef(false)
	const interceptorIdsRef = useRef<{
		axRequest: number
		axResponse: number
		psRequest: number
		psResponse: number
	} | null>(null)
	const [returnTo, setReturnTo] = useAtom(returnToAtom)
	const ejectAuthInterceptors = useCallback(() => {
		const interceptorIds = interceptorIdsRef.current
		if (!interceptorIds) {
			return
		}

		axiosClient.interceptors.request.eject(interceptorIds.axRequest)
		axiosClient.interceptors.response.eject(interceptorIds.axResponse)
		axiosPSClient.interceptors.request.eject(interceptorIds.psRequest)
		axiosPSClient.interceptors.response.eject(interceptorIds.psResponse)
		interceptorIdsRef.current = null
	}, [])

	/**
	 * When login is required the behaviour depends on the context
	 * - Web app: show a pre-login screen
	 * - Electron: show a pre-login screen
	 */
	const handleLoginRequired = () => {
		if (isPixiesetAuthEnabled()) {
			setShowAuthPage(true)
			return
		}

		if (isElectronApp) {
			setShowPreLogin(true)
			return
		}

		loginWithRedirect({
			appState: {
				returnTo: window.location.pathname + window.location.search + window.location.hash,
			},
		}).catch((e) => Sentry.captureException(e))
	}

	/** Determines if error indicates a failure to use Auth0 refresh token.
			Uses same failure detection approach as `auth0-spa-js`.
			See: https://github.com/auth0/auth0-spa-js/blob/8653181d23ecea17729da203253d4fa4d17d8ef6/src/Auth0Client.ts#L997-L1001
		*/
	const isAuth0RefreshTokenError = (error: Error) => {
		return (
			error.message.indexOf(INVALID_REFRESH_TOKEN_ERROR_MESSAGE) > -1 ||
			error.message.indexOf(MISSING_REFRESH_TOKEN_ERROR_MESSAGE) > -1
		)
	}

	const handleAccessTokenRejected = () => {
		if (isPixiesetAuthEnabled()) {
			clearPSAccessToken()
		}
		handleLoginRequired()
	}

	const getRequiredAccessToken = async (request?: PSAccessTokenRequestContext) => {
		try {
			const jwt = await getAccessToken(request)
			if (jwt) {
				return jwt
			}
			handleLoginRequired()
			throw new Error("Access token unavailable")
		} catch (error: unknown) {
			if (isPSAccessTokenRefreshError(error)) {
				if (!error.isTransient) {
					handleLoginRequired()
				}
				throw error
			}
			if (error instanceof Error && isAuth0RefreshTokenError(error)) {
				!isElectronApp && toast(t("appAuthGuard.toastSessionExpired"))
				handleLoginRequired()
			}
			throw error
		}
	}

	const initializeAuthenticatedSession = async () => {
		setShowAuthPage(false)
		setShowPreLogin(false)
		// Eject any previously attached interceptors so re-runs of this effect don't stack
		// duplicate handlers on the shared axios instances.
		ejectAuthInterceptors()
		const axRequest = axiosClient.interceptors.request.use(async (config) => {
			const jwt = await getRequiredAccessToken({
				flow: "photo_editor_api",
				method: config.method?.toUpperCase(),
				path: getTelemetryRequestPath(config.url),
			})
			config.headers.setAuthorization(`Bearer ${jwt}`)
			return config
		})
		const axResponse = axiosClient.interceptors.response.use(
			(response) => response,
			createAxiosClientResponseErrorHandler({
				onLoginRequired: handleAccessTokenRejected,
				onAccountMigrated: () => store.set(isAccountMigratedAtom, true),
			}),
		)

		const psRequest = axiosPSClient.interceptors.request.use(async (config) => {
			config.headers.set("X-PS-Auth-Provider", "OAS")
			config.headers.set("Content-Type", "application/json")
			config.headers.setContentType("application/json")

			const jwt = await getRequiredAccessToken({
				flow: "pixieset_api",
				method: config.method?.toUpperCase(),
				path: getTelemetryRequestPath(config.url),
			})
			config.headers.setAuthorization(`Bearer ${jwt}`)
			return config
		})
		const psResponse = axiosPSClient.interceptors.response.use(
			(response) => response,
			createAxiosPSClientResponseErrorHandler({
				onLoginRequired: handleAccessTokenRejected,
			}),
		)
		interceptorIdsRef.current = { axRequest, axResponse, psRequest, psResponse }
		store.set(isAuthReadyAtom, true)
		setInitialized(true)
		await forceRefreshUser()
		prefetchCurrentUser()
		ProjectService.prefetchProjects()
	}

	const handleAuth = async (url?: URL) => {
		const currentUrl = url ?? new URL(location.href)
		const code = currentUrl.searchParams.get("code")
		const hasPixiesetAuthCode = isPixiesetAuthEnabled(currentUrl) && code !== null
		const hasAuthToken = hasPixiesetAuthCode
			? false
			: url
				? isURLAuthenticated(url)
				: isAuthenticated
		if (hasAuthToken) {
			await initializeAuthenticatedSession()
		} else {
			resetDialogs()
			ejectAuthInterceptors()
			setInitialized(false)
			store.set(isAuthReadyAtom, false)
			store.set(isAccountMigratedAtom, false)

			if (isPixiesetAuthEnabled(currentUrl)) {
				if (code) {
					setShowAuthPage(false)
					setShowPreLogin(false)
					if (isProcessingAuthCodeRef.current) {
						console.debug(
							"Skipping duplicate Pixieset auth callback handling while a request is in flight.", // i18n-ignore
						)
						return
					}
					isProcessingAuthCodeRef.current = true
					// Clean up auth params so refresh doesn't re-run the callback.
					const clearAuthCodeFromUrl = () => {
						currentUrl.searchParams.delete("code")
						currentUrl.searchParams.delete("state")
						window.history.replaceState({}, document.title, currentUrl)
					}
					void requestPSAccessToken(currentUrl)
						.then(async () => {
							//Clearing the code from the URL after successful login
							clearAuthCodeFromUrl()
							sessionStorage.removeItem(MISSING_STATE_RETRY_KEY)
							await initializeAuthenticatedSession()
						})
						.catch((error: unknown) => {
							const isMissingState =
								error instanceof Error && error.message === "Missing state"
							const isUnknownState =
								error instanceof Error &&
								error.message === "Unknown or expired state"
							const isStateIssue = isMissingState || isUnknownState
							if (!isStateIssue) {
								console.error("Error during Pixieset authentication", error)
							} else {
								console.warn(
									"Pixieset auth callback state invalid or missing. Forcing re-login.", // i18n-ignore
								)
							}
							clearAuthCodeFromUrl()
							if (isStateIssue && !isElectronApp) {
								const alreadyRetried =
									sessionStorage.getItem(MISSING_STATE_RETRY_KEY) === "1"
								if (!alreadyRetried) {
									sessionStorage.setItem(MISSING_STATE_RETRY_KEY, "1")
									loginWithRedirect({
										appState: {
											returnTo:
												window.location.pathname +
												window.location.search +
												window.location.hash,
										},
									}).catch((e) => Sentry.captureException(e))
									return
								}
							}
							if (!isElectronApp) {
								const message =
									error instanceof Error &&
									error.message === "Code verifier not found"
										? t("appAuthGuard.toastSessionExpired")
										: t("appAuthGuard.toastLoginFailed")
								if (!isStateIssue) {
									toast(message)
								}
							}
							handleLoginRequired()
						})
						.finally(() => {
							isProcessingAuthCodeRef.current = false
						})
				} else {
					handleLoginRequired()
				}
			} else {
				handleLoginRequired()
			}
		}
	}

	useEffect(() => {
		return () => {
			ejectAuthInterceptors()
			store.set(isAuthReadyAtom, false)
		}
	}, [ejectAuthInterceptors])

	useEffect(() => {
		// If we have a returnTo, update url so we can retrieve full context
		if (isLoading) return
		if (returnTo) {
			const currentURL = new URL(location.href)
			const returnToUrl = new URL(location.origin + returnTo)
			const combined = new URLSearchParams({
				...Object.fromEntries(returnToUrl.searchParams),
				...Object.fromEntries(currentURL.searchParams),
			})

			const url = new URL(returnToUrl)
			url.search = combined.toString()
			setReturnTo(null)
			window.history.replaceState({}, document.title, url)
			void handleAuth(url)
			return
		}

		void handleAuth()
	}, [isLoading, isAuthenticated])

	if (showAuthPage) {
		return <AuthPage />
	}
	if (showPreLogin && isElectronApp) {
		return <ElectronPreLogin />
	}

	if (!initialized) {
		return null
	}

	return <>{children}</>
}
